// Cart Checkout UI. Gating, submission and error vocabulary all come from the
// Checkout service layer (useCheckout); payment reuses RazorpayPaymentPanel.
import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { z } from "zod";

import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { SiteLayout } from "@/components/SiteLayout";
import { Section } from "@/components/Section";
import { PageHero } from "@/components/PageHero";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { useAlacarteCart } from "@/hooks/useAlacarteCart";
import { useCheckout } from "@/hooks/useCheckout";
import { cartRequiresAccount } from "@/lib/checkout/account-rule";
import { describeDrift, friendlyCheckoutError } from "@/lib/checkout/checkout-errors";
import { RazorpayPaymentPanel } from "@/components/payments/RazorpayPaymentPanel";
import { DeliveryDetailsForm } from "@/components/checkout/DeliveryDetailsForm";
import { DeliveryDetailsSummary } from "@/components/checkout/DeliveryDetailsSummary";
import { SavedAddressPicker } from "@/components/checkout/SavedAddressPicker";
import {
  isDeliveryAddressComplete,
  isDeliveryAreaSynced,
  resolveAddressAreaId,
  toCheckoutCustomer,
  type DeliveryAreaOption,
} from "@/lib/checkout/delivery-details";
import { useDeliveryAreas } from "@/hooks/useDeliveryAreas";
import { useCustomerSession } from "@/hooks/useCustomerSession";
import {
  sameAddress,
  toDeliveryAddress,
  upsertPreferredAddress,
} from "@/lib/checkout/saved-address";
import { listCustomerAddresses, type CustomerAddress } from "@/lib/customer/customer-api";
import { useQuery } from "@tanstack/react-query";
import { friendlyCartError } from "@/lib/mp2/cart-errors";
import type { DeliveryAddress } from "@/lib/public/mp2-alacarte-cart.functions";

import { supabase } from "@/integrations/supabase/client";
import {
  WalletRedemption,
  maxApplicablePaise,
  type WalletSummary,
} from "@/components/checkout/WalletRedemption";
import { CouponSection } from "@/components/checkout/CouponSection";
import { computeCouponDiscountPaise } from "@/lib/checkout/coupon-discount";
import { CheckoutCouponPromo } from "@/components/checkout/CheckoutCouponPromo";
import { OrderSummary, type AmountBreakdown } from "@/components/checkout/OrderSummary";
import { cartPriceBreakdown } from "@/lib/public/mp2-alacarte-cart.functions";
import { useServerFn } from "@tanstack/react-start";
import { formatPaise } from "@/lib/format/money";




const checkoutSearchSchema = z.object({
  submission: z.string().optional(),
  source: z.string().optional(),
  cart_token: z.string().optional(),
});

export const Route = createFileRoute("/checkout")({
  validateSearch: checkoutSearchSchema,

  head: () => ({
    meta: [
      { title: "Checkout · Poonam Kapur Healthy Kitchen" },
      {
        name: "description",
        content:
          "Confirm your delivery details and pay securely for your Poonam Kapur Healthy Kitchen order.",
      },
      { property: "og:title", content: "Checkout · Poonam Kapur Healthy Kitchen" },
      {
        property: "og:description",
        content: "Confirm your delivery details and pay securely for your order.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: CheckoutPage,
});

function formatPrice(paise: number) {
  return `₹${(paise / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

type Step = "details" | "wallet" | "payment" | "done";

function checkoutReturnPath() {
  if (typeof window === "undefined") return "/checkout";
  return window.location.pathname + window.location.search;
}

function CheckoutPage() {
  const cart = useAlacarteCart();
  const search = useSearch({ from: "/checkout" });
  const checkout = useCheckout(search.cart_token || cart.token);
  const navigate = useNavigate();

  const qc = useQueryClient();
  const { areas: rawAreas, isFallback, isLoading: areasLoading } = useDeliveryAreas();
  // A fallback list means the area query failed — never accept an unverified area.
  const areas: DeliveryAreaOption[] = isFallback ? [] : rawAreas;
  const areasError = isFallback;

  const [step, setStep] = useState<Step>("details");
  const [paymentToken, setPaymentToken] = useState<string | null>(null);
  const [orderRef, setOrderRef] = useState<string | null>(null);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [submissionId, setSubmissionId] = useState<string>("");
  const [appliedCoupon, setAppliedCoupon] = useState<any | null>(null);
  const [walletAmount, setWalletAmount] = useState(0);
  const breakdownFn = useServerFn(cartPriceBreakdown);
  const breakdownToken = search.cart_token || cart.token;
  const breakdownQuery = useQuery({
    queryKey: [
      "checkout-breakdown",
      breakdownToken,
      cart.snapshot?.cart.delivery_area_id,
      (cart.snapshot?.items ?? []).map((i) => `${i.id}:${i.quantity}:${i.line_total_paise}`).join("|"),
    ],
    enabled: !!breakdownToken && (cart.snapshot?.items.length ?? 0) > 0,
    queryFn: async () => {
      const r = await breakdownFn({ data: { token: breakdownToken! } });
      return "error" in r ? null : r;
    },
  });
  const breakdown: AmountBreakdown | null = breakdownQuery.data ?? null;
  const [submittedBreakdown, setSubmittedBreakdown] = useState<AmountBreakdown | null>(null);
  const [submittedDiscount, setSubmittedDiscount] = useState(0);
  const hasPlan = cartRequiresAccount(cart.snapshot?.items ?? []);
  const hasDish = (cart.snapshot?.items ?? []).some((i) => i.kind !== "meal_plan");
  const itemsLabel = hasPlan && !hasDish ? "Meal plan amount" : hasPlan ? "Meal plan & dish amount" : "Item / dish amount";
  const [paymentMethod, setPaymentMethod] = useState<"razorpay" | "neft" | "cod" | "wallet">("razorpay");
  const [codAck, setCodAck] = useState(false);
  const [neftReference, setNeftReference] = useState("");
  const [paymentConfig, setPaymentConfig] = useState<{
    bank_name?: string | null;
    bank_account_name?: string | null;
    bank_account_no?: string | null;
    bank_ifsc_code?: string | null;
    payu_enabled?: boolean;
    cod_min_order_paise?: number | null;
  } | null>(null);

  // Fetch payment settings (bank details)
  useEffect(() => {
    // We select safely to avoid crashing if columns are missing
    supabase
      .from("site_settings")
      .select("*")
      .eq("id", 1)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setPaymentConfig(data as any);
      });
  }, []);





  // One idempotency key per checkout attempt; regenerated after a hard failure.
  useEffect(() => {
    if (search.submission) {
      setSubmissionId(search.submission);
    } else {
      setSubmissionId(crypto.randomUUID());
    }
  }, [search.submission]);


  const snapshot = cart.snapshot;
  const addr = snapshot?.cart.delivery_address ?? null;
  const areaSynced = isDeliveryAreaSynced(areas, addr, snapshot?.cart.delivery_area_id);
  const hasSavedAddress =
    isDeliveryAddressComplete(addr) && (areasLoading || areaSynced || !snapshot?.cart.delivery_area_id);

  // Checkout never keeps its own address state — the cart row is canonical.
  const [editingAddress, setEditingAddress] = useState(false);
  const [addingAddress, setAddingAddress] = useState(false);
  const [addressSaving, setAddressSaving] = useState(false);
  const [applyingId, setApplyingId] = useState<string | null>(null);
  const [addressError, setAddressError] = useState<string | null>(null);

  // --- Saved address reuse (signed-in customers only; guests unaffected) ---
  const { user, isLoading: sessionLoading } = useCustomerSession();
  const savedAddressQuery = useQuery<CustomerAddress[]>({
    queryKey: ["customer", "addresses", user?.id ?? null],
    queryFn: listCustomerAddresses,
    enabled: Boolean(user),
    staleTime: 60_000,
  });
  const savedAddresses = savedAddressQuery.data ?? [];
  const savedAddress =
    savedAddresses.find((a) => a.is_default) ?? savedAddresses[0] ?? null;
  const [autoApplied, setAutoApplied] = useState(false);

  // Which saved address the cart currently points at (for the radio state).
  const selectedAddressId =
    (addr && savedAddresses.find((a) => sameAddress(toDeliveryAddress(a), addr))?.id) ?? null;

  useEffect(() => {
    if (hasSavedAddress) setEditingAddress(false);
  }, [hasSavedAddress]);



  /** Persists edited details back to the cart before they are ever submitted. */
  async function persistAddress(next: DeliveryAddress, areaId: string, skipSync = false) {
    setAddressSaving(true);
    setAddressError(null);
    try {
      if (snapshot && areaId !== snapshot.cart.delivery_area_id) {
        // Keep the cart delivery zone in step with the chosen area; the
        // delivery date is preserved.
        const ctxRes = await cart.updateContext({
          area_id: areaId,
          date: snapshot.cart.delivery_date,
        });
        if (ctxRes && typeof ctxRes === "object" && "error" in ctxRes) {
          setAddressError(friendlyCartError((ctxRes as { error: string }).error));
          return false;
        }
      }
      const res = await cart.setDeliveryAddress(next);
      if (res && typeof res === "object" && "error" in res && (res as { error?: string }).error) {
        setAddressError(friendlyCartError((res as { error: string }).error));
        return false;
      }
      // Mirror into the address book — updates in place, never duplicates.
      if (user && !skipSync) {
        try {
          await upsertPreferredAddress(next, areaId, {
            targetId: editingAddress && !addingAddress ? selectedAddressId : null,
            forceNew: addingAddress,
          });
          await savedAddressQuery.refetch();
        } catch (err) {
          console.error("[checkout] address-book sync failed", err);
        }
      }

      setEditingAddress(false);
      setAddingAddress(false);
      return true;
    } catch (err) {
      setAddressError(friendlyCartError((err as Error)?.message ?? "unknown_error"));
      return false;
    } finally {
      setAddressSaving(false);
    }
  }

  /** Applies a saved address-book row to the cart (radio selection). */
  async function applySavedAddress(row: CustomerAddress) {
    const next = toDeliveryAddress(row, {
      name: user?.user_metadata?.["full_name"] as string | undefined,
      email: user?.email ?? "",
    });
    const areaId = row.delivery_area_id ?? resolveAddressAreaId(areas, next) ?? null;
    if (!areaId) {
      setAddressError(friendlyCartError("unavailable:area_not_served"));
      return;
    }
    setApplyingId(row.id);
    try {
      // Use skipSync=true because we are applying an existing address, 
      // not creating or modifying one.
      await persistAddress(next, areaId, true);
    } finally {
      setApplyingId(null);
    }
  }

  // Auto-load the customer's default address into an empty cart exactly once.
  useEffect(() => {
    if (autoApplied || !user || !snapshot || areasLoading || areasError) return;
    if (isDeliveryAddressComplete(addr)) return;
    if (!savedAddress) return;
    const next = toDeliveryAddress(savedAddress, {
      name: user.user_metadata?.["full_name"] as string | undefined,
      email: user.email ?? "",
    });
    const areaId =
      savedAddress.delivery_area_id ?? resolveAddressAreaId(areas, next) ?? null;
    
    // We auto-apply even if areaId is missing, because persistAddress handles the error
    // and let the user correct it in the UI.
    setAutoApplied(true);
    if (areaId) {
      void persistAddress(next, areaId, true);
    } else {
      // Fallback: apply the address content even if area resolution failed
      void cart.setDeliveryAddress(next);
    }
  }, [autoApplied, user, snapshot, areasLoading, areasError, addr, savedAddress, areas]);


  // --- Wallet redemption (signed-in customers only; guests unaffected) ---
  const walletQuery = useQuery({
    queryKey: ["customer", "wallet", "checkout", user?.id ?? null],
    enabled: Boolean(user),
    staleTime: 30_000,
    queryFn: async (): Promise<WalletSummary | null> => {
      const { data, error } = await supabase.rpc("customer_get_wallet" as never);
      if (error) throw new Error(error.message);
      return (data ?? null) as WalletSummary | null;
    },
  });
  const wallet = walletQuery.data ?? null;
  const walletUsable = Boolean(
    wallet &&
      wallet.enabled &&
      wallet.checkout_redemption_enabled &&
      !wallet.is_frozen
  );
  
  const walletInsufficient = walletUsable && wallet && wallet.balance_paise < walletAmount;



  async function submitOrder(address: DeliveryAddress) {
    if (checkout.isSubmitting) return;
    
    const cartToken = search.cart_token || cart.token;

    const res = await checkout.submit(
      { ...toCheckoutCustomer(address), cod_acknowledgement: codAck }, 
      submissionId, 
      cartToken,
      {
        couponCode: appliedCoupon?.code,
        walletAmountPaise: walletAmount,
        paymentMethod,
        neftReference: paymentMethod === "neft" ? neftReference : undefined,
      }
    );
    if (res?.ok) {
      setSubmittedBreakdown(breakdown);
      setSubmittedDiscount(res.couponDiscountPaise ?? 0);
      setOrderRef(res.orderRef);
      setOrderId(res.orderId);
      setPaymentToken(res.paymentToken);
      
      if (paymentMethod === "razorpay" && res.paymentToken) {
        // Token exchange is now handled entirely by the RazorpayPaymentPanel component.
        setStep("payment");
      } else {
        setStep("payment");
      }

    } else {
      setSubmissionId(crypto.randomUUID());
    }
  }




  // Server-configured COD minimum (site_settings.cod_min_order_paise). When
  // the setting is missing/invalid the server rejects COD fail-closed, so the
  // UI disables it too rather than offering an option that cannot succeed.
  const codMinimumPaise =
    typeof paymentConfig?.cod_min_order_paise === "number" &&
    paymentConfig.cod_min_order_paise > 0
      ? paymentConfig.cod_min_order_paise
      : null;

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Checkout"
        title={search.source === "renewal" ? "Renew your subscription" : "Complete your order"}
        intro={search.source === "renewal" 
          ? "Confirm your details to renew your meal plan." 
          : "Confirm your delivery details and proceed to payment."}

      />
      <Section>
        {checkout.isModeLoading || cart.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading checkout…</p>
        ) : hasPlan && !user && !sessionLoading ? (
          <Alert>
            <AlertTitle>Please sign in to order a meal plan</AlertTitle>
            <AlertDescription>
              {friendlyCheckoutError("meal_plan_sign_in_required")}
              <div className="mt-3 flex flex-wrap gap-3">
                <Button asChild size="sm">
                  <Link to="/account/sign-in" search={{ redirect: checkoutReturnPath() } as never}>
                    Sign in
                  </Link>
                </Button>
                <Button asChild size="sm" variant="outline">
                  <Link to="/account/sign-up" search={{ redirect: checkoutReturnPath() } as never}>
                    Create account
                  </Link>
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        ) : hasPlan && sessionLoading ? (
          <p className="text-sm text-muted-foreground">Loading checkout…</p>
        ) : !checkout.isCheckoutEnabled ? (
          <Alert>
            <AlertTitle>Checkout isn't available yet</AlertTitle>
            <AlertDescription>
              {friendlyCheckoutError("checkout_disabled")}
              <div className="mt-3 flex gap-3">
                <Link
                  to="/cart"
                  className="inline-flex items-center rounded-full border border-primary px-5 py-2 text-sm font-medium text-primary"
                >
                  Back to cart
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground"
                >
                  Contact us
                </Link>
              </div>
            </AlertDescription>
          </Alert>
        ) : step === "done" ? (
          <Alert>
            <AlertTitle>Order confirmed</AlertTitle>
            <AlertDescription>
              Thank you! Your payment for order{" "}
              <span className="font-medium">{orderRef}</span> has been received. We'll
              confirm delivery details shortly.
              <div className="mt-3">
                <Link
                  to="/"
                  className="inline-flex items-center rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground"
                >
                  Back to home
                </Link>
              </div>
            </AlertDescription>
          </Alert>
        ) : step === "wallet" && orderId && wallet ? (
          <WalletRedemption
            orderId={orderId}
            orderTotalPaise={cart.totalPaise}
            wallet={wallet}
            onApplied={() => {
              void walletQuery.refetch();
            }}
            onContinue={() => setStep("payment")}
          />
        ) : step === "payment" && (paymentToken || paymentMethod !== "razorpay") ? (
          paymentMethod === "razorpay" ? (
            <div className="mx-auto max-w-xl space-y-6">
            {submittedBreakdown ? (
              <OrderSummary breakdown={submittedBreakdown} discount={submittedDiscount} itemsLabel={itemsLabel} title="Amount breakdown" />
            ) : null}
            <RazorpayPaymentPanel
              token={paymentToken!}
              cleanUrl="/checkout"
              title="Pay for your order"
              onSuccess={() => {
                cart.resetAfterCheckout();
                setStep("done");
              }}
              successActions={() => (
                <Link
                  to="/"
                  className="mt-6 inline-flex items-center justify-center rounded-md border px-4 py-2 text-sm"
                >
                  Back to home
                </Link>
              )}
              errorActions={() => (
                <button
                  type="button"
                  onClick={() => void navigate({ to: "/cart" })}
                  className="inline-flex items-center justify-center rounded-md border px-4 py-2 text-sm"
                >
                  Back to cart
                </button>
              )}
            />
            </div>
          ) : (
            <div className="mx-auto max-w-xl rounded-lg border bg-card p-6 shadow-sm text-center">
              <h2 className="text-xl font-semibold mb-4">
                {paymentMethod === "cod" ? "Order Received!" : "Order Received!"}
              </h2>
              <p className="text-muted-foreground mb-6">
                {paymentMethod === "cod" 
                  ? "Your Cash on Delivery order has been placed. Please call our Admin / Back Office team to confirm your order. Your order will remain on hold until our team confirms it."
                  : `Your order ${orderRef} has been successfully placed via ${paymentMethod.replace(/_/g, " ")}.`
                }
              </p>
              {paymentMethod === "cod" && (
                <div className="mb-6 p-4 bg-muted rounded-md text-sm font-medium">
                  Order Reference: {orderRef}
                </div>
              )}
              {paymentMethod === "neft" && (
                <Alert className="mb-6 bg-blue-50/50 border-blue-200">
                  <AlertTitle className="text-blue-800">Verification Pending</AlertTitle>
                  <AlertDescription className="text-blue-700">
                    We will process your order once the NEFT transfer (Ref: {neftReference}) is verified by our team.
                  </AlertDescription>
                </Alert>
              )}
              {paymentMethod === "cod" && (
                <p className="text-sm text-muted-foreground mb-6">
                  Please keep the cash ready at the time of delivery.
                </p>
              )}
              <div className="flex flex-col gap-3">
                <Button asChild>
                  <Link to="/" onClick={() => cart.resetAfterCheckout()}>Back to Home</Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/contact">Support</Link>
                </Button>
              </div>
            </div>
          )
        ) : (!snapshot || snapshot.items.length === 0) && search.source !== "renewal" ? (
          <Alert>
            <AlertTitle>{"\n"}</AlertTitle>
            <AlertDescription>
              {friendlyCheckoutError("cart_empty")}
              <div className="mt-3">
                <Link
                  to="/products"
                  className="inline-flex items-center rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground"
                >
                  Explore à la carte
                </Link>
              </div>
            </AlertDescription>
          </Alert>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
            <div className="space-y-4">
              {/* Returning customers pick from the address book; the blank
                  form only appears for first-time customers or on request. */}
              {savedAddresses.length > 0 && !editingAddress && !addingAddress ? (
                <>
                  <SavedAddressPicker
                    addresses={savedAddresses}
                    selectedId={selectedAddressId}
                    busyId={applyingId}
                    onSelect={(a) => {
                      checkout.clearError();
                      void applySavedAddress(a);
                    }}
                    onEdit={(a) => {
                      checkout.clearError();
                      if (a.id !== selectedAddressId) void applySavedAddress(a);
                      setEditingAddress(true);
                    }}
                    onAddNew={() => {
                      checkout.clearError();
                      setAddressError(null);
                      setAddingAddress(true);
                    }}
                  />
                  {addressError ? (
                    <div
                      role="alert"
                      className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
                    >
                      {addressError}
                    </div>
                  ) : null}
                  {hasSavedAddress && addr ? (
                    <div className="mt-4 space-y-6">
                      <DeliveryDetailsSummary
                        address={addr}
                        title="Selected address"
                        savedAt={snapshot?.cart.delivery_address_updated_at}
                        editLabel="Modify this address"
                        onEdit={() => {
                          checkout.clearError();
                          setEditingAddress(true);
                        }}
                      />
                      
                      <CheckoutCouponPromo placement="above_input" />
                      <CouponSection 
                        onApply={(c) => {
                          setAppliedCoupon(c);
                          // Enforce stacking rule: if coupon doesn't allow wallet, clear wallet
                          if (c && !c.stack_with_wallet) {
                            setWalletAmount(0);
                          }
                        }}
                        subtotalPaise={cart.totalPaise}
                        customerId={user?.id}
                      />

                      {false && walletUsable && (appliedCoupon?.stack_with_wallet !== false) && (
                        <div className="space-y-3 rounded-xl border border-border/70 bg-card p-4">
                          <Label className="text-sm font-medium">Use wallet balance</Label>
                          <div className="flex items-center gap-3">
                            <Input
                              type="number"
                              placeholder="Amount (₹)"
                              value={walletAmount > 0 ? walletAmount / 100 : ""}
                              onChange={(e) => {
                                const val = Math.max(0, parseInt(e.target.value || "0") * 100);
                                const cap = maxApplicablePaise(wallet!, cart.totalPaise);
                                setWalletAmount(Math.min(val, cap));
                              }}
                              className="w-32"
                            />
                            <div className="text-xs text-muted-foreground">
                              Max {formatPaise(maxApplicablePaise(wallet!, cart.totalPaise))} applicable
                            </div>
                            {walletAmount > 0 && (
                              <Button variant="ghost" size="sm" onClick={() => setWalletAmount(0)}>
                                Clear
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <div className="space-y-4 rounded-xl border border-border/70 bg-card p-6 shadow-sm">
                        <h3 className="font-semibold">Select Payment Method</h3>
                        <div className="grid gap-4 sm:grid-cols-2">
                          {[
                            { id: "razorpay", label: "Razorpay (Cards/UPI)", desc: "Pay securely via Razorpay" },
                            { id: "neft", label: "NEFT / Bank Transfer", desc: "Manual bank transfer" },
                            { 
                              id: "cod", 
                              label: "Cash on Delivery", 
                              desc: "Pay at your doorstep",
                              disabled: !breakdown || codMinimumPaise === null || Math.max(0, breakdown.totalPaise
                                - computeCouponDiscountPaise(appliedCoupon, cart.totalPaise)
                                - walletAmount) < codMinimumPaise
                            },
                            // { id: "wallet", label: "Wallet Only", desc: "Pay using wallet balance" },
                          ].map((method) => {
                            const isActive = paymentMethod === method.id;
                            const isDisabled = (method as any).disabled;
                            return (
                              <button
                                key={method.id}
                                type="button"
                                disabled={isDisabled}
                                data-method={method.id}
                                onClick={() => {
                                  setPaymentMethod(method.id as any);
                                }}
                                className={`flex flex-col items-start rounded-lg border p-4 text-left transition-colors ${
                                  isDisabled ? "opacity-50 cursor-not-allowed bg-muted" :
                                  isActive
                                    ? "border-primary bg-primary/5 ring-1 ring-primary"
                                    : "border-border hover:border-primary/50"
                                }`}
                              >
                                <span className="font-medium">{method.label}</span>
                                <span className="text-xs text-muted-foreground">
                                  {isDisabled
                                    ? codMinimumPaise === null
                                      ? "COD is temporarily unavailable"
                                      : `Minimum order ${formatPaise(codMinimumPaise)} for COD`
                                    : method.desc}
                                </span>
                              </button>
                            );
                          })}
                        </div>

                        {paymentMethod === "cod" && (
                          <div className="mt-4 space-y-4 rounded-lg bg-orange-50/50 p-4 border border-orange-100">
                            <h4 className="text-sm font-semibold text-orange-900">COD Acknowledgement</h4>
                            <p className="text-xs text-orange-800">
                              Cash on Delivery orders require phone confirmation from our Admin / Back Office team.
                            </p>
                            <div className="flex items-start space-x-2 pt-1">
                              <Checkbox 
                                id="cod-ack" 
                                checked={codAck}
                                onCheckedChange={(checked: boolean) => setCodAck(!!checked)}
                              />
                              <label
                                htmlFor="cod-ack"
                                className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-orange-900 cursor-pointer"
                              >
                                I understand that I must call Admin / Back Office to confirm this COD order. My order will remain on hold until it is manually confirmed.
                              </label>
                            </div>
                          </div>
                        )}


                        {paymentMethod === "neft" && (
                          <div className="mt-4 space-y-4 rounded-lg bg-muted/50 p-4 border">
                            <h4 className="text-sm font-semibold">Bank Details for NEFT</h4>
                            <div className="grid gap-2 text-xs">
                              <div className="flex justify-between"><span className="text-muted-foreground">Bank:</span> <span>{paymentConfig?.bank_name || "—"}</span></div>
                              <div className="flex justify-between"><span className="text-muted-foreground">Account Name:</span> <span>{paymentConfig?.bank_account_name || "—"}</span></div>
                              <div className="flex justify-between"><span className="text-muted-foreground">Account No:</span> <span>{paymentConfig?.bank_account_no || "—"}</span></div>
                              <div className="flex justify-between"><span className="text-muted-foreground">IFSC:</span> <span>{paymentConfig?.bank_ifsc_code || "—"}</span></div>
                            </div>

                            <div className="space-y-2 pt-2">
                              <Label htmlFor="neft-ref" className="text-xs">Transaction Reference No (Required)</Label>
                              <Input 
                                id="neft-ref"
                                placeholder="UTR / Ref No."
                                value={neftReference}
                                onChange={(e) => setNeftReference(e.target.value)}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-dashed border-border/70 p-6 text-center text-sm text-muted-foreground">
                      Please select an address above to continue.
                    </div>
                  )}

                </>
              ) : !user || !hasSavedAddress || editingAddress || addingAddress || !addr ? (
                <div className="rounded-2xl border border-border/70 bg-card p-6">
                  <DeliveryDetailsForm
                    idPrefix="co"
                    initial={addingAddress ? null : addr}
                    areas={areas}
                    areasLoading={areasLoading}
                    areasError={areasError}
                    onRetryAreas={() => {
                      void qc.invalidateQueries({ queryKey: ["delivery-areas", "public"] });
                    }}
                    cartAreaId={snapshot?.cart.delivery_area_id}

                    title={addingAddress ? "Add a new address" : "Delivery details"}
                    description="We'll use these details for delivery and order updates."
                    submitLabel={hasSavedAddress && !addingAddress ? "Save changes" : "Save and continue"}
                    submittingLabel="Saving…"
                    saving={addressSaving || checkout.isSubmitting}
                    error={addressError}
                  onCancel={
                    hasSavedAddress || savedAddresses.length > 0
                      ? () => {
                          setEditingAddress(false);
                          setAddingAddress(false);
                          setAddressError(null);
                        }
                      : undefined
                  }
                  onSubmit={async (next, areaId) => {
                    await persistAddress(next, areaId);
                  }}
                />
                {user && (
                  <div className="mt-4 border-t border-border/70 pt-4">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingAddress(false);
                        setAddingAddress(false);
                      }}
                    >
                      Cancel and use saved address
                    </Button>
                  </div>
                )}
              </div>
              ) : (
                <DeliveryDetailsSummary
                  address={addr}
                  title="Delivery details"
                  savedAt={snapshot?.cart.delivery_address_updated_at}
                  onEdit={() => {
                    checkout.clearError();
                    setEditingAddress(true);
                  }}
                />
              )}


              {checkout.error ? (
                <div
                  role="alert"
                  className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
                >
                  {checkout.error}
                  {checkout.drift && checkout.drift.length > 0 ? (
                    <ul className="mt-2 list-disc pl-5">
                      {checkout.drift.map((d, i) => (
                        <li key={d.item_id ?? i}>{describeDrift(d)}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}

              <div className="flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  disabled={checkout.isSubmitting || addressSaving || areasLoading}
                  onClick={() => {
                    if (areasError) {
                      setAddressError(
                        "We couldn't load the delivery areas, so we can't confirm your delivery zone. Please retry before continuing.",
                      );
                      setEditingAddress(true);
                      return;
                    }
                    if (!addr || !hasSavedAddress) {
                      setEditingAddress(true);
                      setAddressError(
                        addr && !areaSynced
                          ? "Please confirm your delivery area below before continuing to payment."
                          : "Please add your delivery details below before continuing to payment.",
                      );
                      return;
                    }
                    if (paymentMethod === "neft" && !neftReference.trim()) {
                      setAddressError("Please enter the NEFT Transaction Reference No.");
                      return;
                    }
                    if (paymentMethod === "wallet" && walletInsufficient) {
                      setAddressError("Insufficient wallet balance for this payment.");
                      return;
                    }
                    if (paymentMethod === "cod" && !codAck) {
                      setAddressError("Please acknowledge the phone-confirmation requirement for COD.");
                      return;
                    }
                    void submitOrder(addr);
                  }}
                >
                  {checkout.isSubmitting ? "Placing order…" : "Continue to payment"}
                </Button>
                <Link
                  to="/cart"
                  className="text-sm text-muted-foreground underline underline-offset-4"
                >
                  Back to cart
                </Link>
              </div>
            </div>

            <aside className="space-y-6">
              <div className="h-fit rounded-2xl border border-border/70 bg-card p-6 shadow-sm">
                <h2 className="text-lg font-semibold">Your items</h2>
                <ul className="mt-4 space-y-2 text-sm">
                  {snapshot?.items.map((it) => (
                    <li key={it.id} className="flex justify-between gap-4">
                      <span className="min-w-0 truncate text-muted-foreground">
                        {(it.kind === "meal_plan"
                          ? it.meal_plan?.title
                          : (it.dish ?? it.beverage ?? it.soup ?? it.salad)?.name) ?? "Item"}
                        {" × "}
                        {it.kind === "meal_plan" ? (it.plan_quantity ?? it.quantity) : it.quantity}
                      </span>
                      <span>{formatPrice(it.line_total_paise)}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <CheckoutCouponPromo placement="order_summary" />
              <OrderSummary
                breakdown={breakdown}
                loading={breakdownQuery.isFetching}
                itemsLabel={itemsLabel}
                discount={computeCouponDiscountPaise(appliedCoupon, cart.totalPaise)}
                walletApplied={walletAmount}
              />



              {checkout.mode === "test" || checkout.mode === "uat" ? (
                <p className="mt-3 text-xs text-muted-foreground">
                  {checkout.mode === "test"
                    ? "Checkout is in admin test mode. Razorpay runs in test mode — no real money is charged."
                    : "Checkout is in UAT mode. Razorpay runs in test mode — no real money is charged."}
                </p>
              ) : null}
            </aside>
          </div>
        )}
      </Section>
    </SiteLayout>
  );
}
